import React from 'react';
import { StayHour } from '../api.reservations';
import { colors } from '../theme';
import { useLang } from '../i18n/LanguageContext';

interface Props {
  checkin?: StayHour | null;
  checkout?: StayHour | null;
  /** Matches the surrounding date line, which differs between the table and the two card layouts. */
  fontSize?: string;
}

/**
 * The hours a stay actually runs on, under the dates it runs between.
 *
 * <p>Exactly one hour per end, and its colour is the whole message: plain is the apartment's
 * standard, amber is an hour somebody has asked for but nobody has paid for, green is an hour that
 * has genuinely moved. The tick on an amber hour means you have already approved it and the wait is
 * now on the guest.
 *
 * <p>Nothing is written out in words because this line sits in a column you read downwards — the
 * stage and the standard it would replace are in the tooltip, one hover away.
 */

const CHECK = (
  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, display: 'block' }}>
    <path d="M4 12.5l5.5 5.5L20 7" />
  </svg>
);

const TEXT = {
  cs: {
    asked: (h: string, s: string, early: boolean) =>
      `Host žádá ${early ? 'časný check-in' : 'pozdní check-out'} ${h} · standardně ${s} · čeká na vaši odpověď`,
    approved: (h: string, s: string, early: boolean) =>
      `${early ? 'Časný check-in' : 'Pozdní check-out'} ${h} · schváleno, čeká na platbu · standardně ${s}`,
    paid: (h: string, s: string, early: boolean) =>
      `${early ? 'Časný check-in' : 'Pozdní check-out'} ${h} · zaplaceno · standardně ${s}`,
    standard: (h: string, early: boolean) => `${early ? 'Check-in' : 'Check-out'} ${h} · standardní čas`,
  },
  en: {
    asked: (h: string, s: string, early: boolean) =>
      `Guest asked for ${early ? 'early check-in' : 'late check-out'} at ${h} · standard is ${s} · waiting for your answer`,
    approved: (h: string, s: string, early: boolean) =>
      `${early ? 'Early check-in' : 'Late check-out'} ${h} · approved, waiting for payment · standard is ${s}`,
    paid: (h: string, s: string, early: boolean) =>
      `${early ? 'Early check-in' : 'Late check-out'} ${h} · paid · standard is ${s}`,
    standard: (h: string, early: boolean) => `${early ? 'Check-in' : 'Check-out'} ${h} · standard time`,
  },
};

const chipBase: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 3,
  fontWeight: 700,
  borderRadius: 3,
  padding: '0 5px',
  cursor: 'help',
};

const End: React.FC<{ hour: StayHour; early: boolean; t: typeof TEXT['cs'] }> = ({ hour, early, t }) => {
  const { time, state, standard } = hour;

  if (!state) {
    return <span style={{ color: '#718096' }} title={t.standard(time, early)}>{time}</span>;
  }

  const paid = state === 'PAID';
  const approved = state === 'APPROVED';
  const title = paid
    ? t.paid(time, standard, early)
    : approved
      ? t.approved(time, standard, early)
      : t.asked(time, standard, early);

  return (
    <span
      title={title}
      style={{
        ...chipBase,
        background: paid ? colors.badgeGreenBg : colors.badgeOrangeBg,
        color: paid ? colors.badgeGreenText : colors.badgeOrangeText,
      }}
    >
      {approved && CHECK}
      {time}
    </span>
  );
};

const StayHours: React.FC<Props> = ({ checkin, checkout, fontSize = '0.72rem' }) => {
  const { lang } = useLang();
  const t = TEXT[lang === 'cs' ? 'cs' : 'en'];

  // Nothing to draw until the backend has filled these in — an older cached response, or a screen
  // that builds its rows from somewhere other than the list endpoints.
  if (!checkin || !checkout) return null;

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: 4,
      fontSize,
      lineHeight: 1.3,
      marginTop: 1,
      fontVariantNumeric: 'tabular-nums',
      whiteSpace: 'nowrap',
    }}>
      <End hour={checkin} early t={t} />
      <span style={{ color: '#a0aec0' }}>→</span>
      <End hour={checkout} early={false} t={t} />
    </div>
  );
};

export default StayHours;
